import type { PlaybookSelection } from "@repo/app-types/entities/playbook";
import {
  type Signal,
  type SignalMetadataSchema,
  type SignalMetadataSchemaField,
  isValidMetadataField,
} from "@repo/app-types/entities/signal";
import type { SignalProposalData } from "@repo/app-types/entities/signalProposal";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";

/** The signal fields the editor holds. */
export type SignalFormFields = Pick<
  Signal,
  "name" | "description" | "metadata_schema" | "playbook_scope"
>;

/** The fields the editor holds, read off a saved signal. */
export function readFormFields(signal: SignalFormFields): SignalFormFields {
  return {
    name: signal.name,
    description: signal.description,
    metadata_schema: signal.metadata_schema,
    playbook_scope: signal.playbook_scope,
  };
}

/**
 * The form as the proposal would leave it. A proposal covers one part of a
 * signal, so every field it does not name keeps what the form holds — which is
 * what makes a proposal a preview over the draft rather than a replacement of
 * it.
 */
export function mergeProposalIntoForm(
  form: SignalFormFields,
  data: SignalProposalData,
): SignalFormFields {
  switch (data.kind) {
    case "create_signal":
      return {
        ...form,
        name: data.name,
        description: data.description,
        metadata_schema: data.metadata_schema,
      };
    case "update_signal_name":
      return { ...form, name: data.name };
    case "update_signal_description":
      return { ...form, description: data.description };
    case "update_signal_metadata":
      return { ...form, metadata_schema: data.metadata_schema };
  }
}

/** Returns true if the signal form differs from the definition it was loaded from. */
export function isSignalFormDirty(
  savedSignal:
    | Pick<
        Signal,
        "name" | "description" | "metadata_schema" | "playbook_scope"
      >
    | undefined,
  name: string,
  description: string,
  metadataSchema: SignalMetadataSchema,
  playbookScope: PlaybookSelection,
): boolean {
  if (!savedSignal) {
    // New signal: dirty if the user has typed anything
    return (
      name.trim() !== "" ||
      description.trim() !== "" ||
      metadataSchema.fields.length > 0 ||
      playbookScope.type !== "all"
    );
  }
  return (
    name !== savedSignal.name ||
    description !== savedSignal.description ||
    JSON.stringify(metadataSchema) !==
      JSON.stringify(savedSignal.metadata_schema) ||
    JSON.stringify(playbookScope) !== JSON.stringify(savedSignal.playbook_scope)
  );
}

/**
 * Filter out empty keys and empty enum options from metadata schema fields.
 * Preserves each field's stable `id` so edits to the display label don't
 * invalidate previously extracted metadata.
 */
export function cleanMetadataSchema(
  schema: SignalMetadataSchema,
): SignalMetadataSchema {
  const cleaned = schema.fields
    .filter((f) => f.key.trim() !== "")
    .flatMap((f): SignalMetadataSchemaField[] => {
      switch (f.type) {
        case "enum":
          return [{ ...f, options: f.options.filter((o) => o.trim() !== "") }];
        case "number":
          return [f];
        default:
          return assertUnreachable(f, () => []);
      }
    })
    .filter(isValidMetadataField);
  return { fields: cleaned };
}
